import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getEmailApiBaseUrl } from '../../../utils/emailApi';
import LoadingSpinner from '../../common/LoadingSpinner';
import {
  gymSocialCard,
  gymSocialInsetBox,
  gymSocialMutedText,
  gymSocialTitleText,
} from '../gymPageTheme';

let presetsInflight = null;

const PRESET_COPY = {
  'medical_cert.expired': {
    title: 'gym.email.automations.medicalTitle',
    hint: 'gym.email.automations.medicalHint',
    subject: 'Certificato medico scaduto',
    body: 'Ciao {{nome}}, il certificato medico risulta scaduto. Passa in {{palestra}} per rinnovarlo.',
  },
  'subscription.expired': {
    title: 'gym.email.automations.subscriptionTitle',
    hint: 'gym.email.automations.subscriptionHint',
    subject: 'Abbonamento scaduto',
    body: 'Ciao {{nome}}, il tuo abbonamento in {{palestra}} risulta scaduto.',
  },
};

function previewBody(html, fallback) {
  const text = String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .trim();
  return text || fallback;
}

export default function GymEmailAutomationsPanel({ api }) {
  const { t } = useTranslation();
  const emailApi = getEmailApiBaseUrl();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      if (!presetsInflight) {
        presetsInflight = api
          .get('gym_email_automations.php?action=list', { baseURL: emailApi })
          .finally(() => {
            presetsInflight = null;
          });
      }
      const res = await presetsInflight;
      setRules(res.data?.automations || []);
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.automations.loadError'));
    } finally {
      setLoading(false);
    }
  }, [api, emailApi, t]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const handleToggle = async (rule) => {
    setBusyId(rule.id);
    try {
      await api.post(
        'gym_email_automations.php?action=toggle',
        { id: rule.id, is_active: !rule.is_active },
        { baseURL: emailApi }
      );
      setRules((prev) => prev.map((item) => (
        item.id === rule.id ? { ...item, is_active: !item.is_active } : item
      )));
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.automations.saveError'));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-4">
      <p className={`text-sm leading-relaxed ${gymSocialMutedText}`}>
        {t('gym.email.automations.presetIntro')}
      </p>
      {rules.map((rule) => {
        const copy = PRESET_COPY[rule.trigger_code];
        const active = !!rule.is_active;
        return (
          <div key={rule.id} className={`${gymSocialCard} flex items-start justify-between gap-4 p-4`}>
            <div className="min-w-0">
              <h2 className={`text-base font-semibold ${gymSocialTitleText}`}>
                {copy ? t(copy.title) : rule.name}
              </h2>
              <p className={`mt-1 text-sm leading-relaxed ${gymSocialMutedText}`}>
                {copy ? t(copy.hint) : ''}
              </p>
              <div className={`mt-3 space-y-2 p-3 text-sm ${gymSocialInsetBox}`}>
                <p className={gymSocialTitleText}>
                  <span className={gymSocialMutedText}>{t('gym.email.automations.previewSubject')}: </span>
                  {rule.email_subject || copy?.subject}
                </p>
                <p className={`whitespace-pre-line ${gymSocialTitleText}`}>
                  <span className={gymSocialMutedText}>{t('gym.email.automations.previewBody')}: </span>
                  {previewBody(rule.email_body, copy?.body)}
                </p>
                <p className={`text-xs ${gymSocialMutedText}`}>{t('gym.email.automations.previewNote')}</p>
              </div>
            </div>
            <button
              type="button"
              disabled={busyId === rule.id}
              onClick={() => handleToggle(rule)}
              className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium ${
                active ? 'bg-indigo-600 text-white' : 'bg-white/10 text-white/80'
              }`}
              aria-pressed={active}
            >
              {active ? t('gym.email.automations.presetOff') : t('gym.email.automations.presetOn')}
            </button>
          </div>
        );
      })}
    </div>
  );
}
